import React from 'react'
import { Badge } from '@/components/ui/badge'

interface Asset {
  id: string
  name: string
  type: string
  size: number
  status?: 'uploading' | 'analyzing' | 'complete' | 'error'
  saveStatus?: 'saving' | 'saved' | 'failed' | 'unsaved'
  lastSavedAt?: string
  uploadDate: string
  analysis?: {
    summary?: {
      passed: number
      warnings: number
      failed: number
    }
  }
}

interface AssetTableRowProps {
  asset: Asset
  onClick: () => void
}

export default function AssetTableRow({ asset, onClick }: AssetTableRowProps) {
  return (
    <tr
      onClick={onClick}
      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
    >
      {/* Asset Name */}
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900 dark:text-white truncate max-w-xs">
          {asset.name}
        </div>
      </td>
      
      {/* Type */}
      <td className="px-4 py-3">
        <Badge variant="outline" className={
          asset.type === 'image' ? 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700' : 
          asset.type === 'video' ? 'bg-purple-50 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700' : 
          'bg-gray-50 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
        }>
          {asset.type === 'image' ? 'IMG' : asset.type === 'video' ? 'VID' : 'DOC'}
        </Badge>
      </td>
      
      {/* Size */}
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        {Math.round(asset.size / 1024)} KB
      </td>
      
      {/* Status */}
      <td className="px-4 py-3">
        {asset.status === 'complete' ? (
          <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">
            Complete
          </Badge>
        ) : asset.status === 'analyzing' ? (
          <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
            Analyzing
          </Badge>
        ) : asset.status === 'uploading' ? (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700">
            Uploading
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700">
            Error
          </Badge>
        )}
      </td>
      
      {/* Save Status */}
      <td className="px-4 py-3">
        {asset.saveStatus === 'saving' && (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700">
            💾 Saving
          </Badge>
        )}
        {asset.saveStatus === 'saved' && (
          <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">
            ✓ Saved
          </Badge>
        )}
        {asset.saveStatus === 'failed' && (
          <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700">
            ⚠ Failed
          </Badge>
        )}
        {asset.saveStatus === 'unsaved' && (
          <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
            ○ Not saved
          </Badge>
        )}
        {!asset.saveStatus && (
          <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
        )}
      </td>
      
      {/* Compliance */}
      <td className="px-4 py-3">
        {asset.analysis?.summary ? (
          <div className="flex items-center gap-2">
            {asset.analysis.summary.failed > 0 && (
              <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700">
                {asset.analysis.summary.failed} ✗
              </Badge>
            )}
            {asset.analysis.summary.warnings > 0 && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700">
                {asset.analysis.summary.warnings} ⚠
              </Badge>
            )}
            {asset.analysis.summary.passed > 0 && (
              <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">
                {asset.analysis.summary.passed} ✓
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
        )}
      </td>
      
      {/* Date */}
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        {asset.lastSavedAt ? (
          new Date(asset.lastSavedAt).toLocaleString()
        ) : asset.uploadDate ? (
          new Date(asset.uploadDate).toLocaleDateString()
        ) : (
          '-'
        )}
      </td>
    </tr>
  )
}


